import React from 'react'
import {Link} from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {HelpTableOfContents} from './HelpTableOfContents'
import {HelpFAQSection} from './HelpFAQSection'
import {HelpLanguageSelector} from './HelpLanguageSelector'
import {KTIcon} from '../../../_metronic/helpers/components/KTIcon'

interface FAQ {
  question: string
  answer: string
}

interface HelpContent {
  title: string
  shortDescription: string
  markdownBody: string
  faqs: FAQ[]
}

interface Language {
  code: string
  name: string
  nativeName: string
  isDefault: boolean
}

interface HelpDocument {
  pageId: string
  category: string
  icon: string
  order: number
  tags: string[]
  relatedPages: string[]
  availableLanguages: string[]
  content: HelpContent
}

interface Props {
  document: HelpDocument
  languages: Language[]
  currentLang: string
  onLangChange: (lang: string) => void
}

/** Custom heading renderer that adds IDs for anchor links */
const createHeadingRenderer = (level: number) => {
  const HeadingComponent: React.FC<React.PropsWithChildren<{}>> = ({children}) => {
    const text = extractText(children)
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
    const Tag = `h${level}` as keyof JSX.IntrinsicElements
    return <Tag id={id}>{children}</Tag>
  }
  return HeadingComponent
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (React.isValidElement(node) && node.props.children) {
    return extractText(node.props.children)
  }
  return ''
}

const HelpDocumentView: React.FC<Props> = ({document: doc, languages, currentLang, onLangChange}) => {
  const {content, relatedPages, availableLanguages} = doc
  const hasFaqs = content.faqs && content.faqs.length > 0

  return (
    <div className='help-document'>
      <div className='d-flex align-items-start justify-content-between mb-2 flex-wrap gap-2'>
        <div className='d-flex align-items-center gap-3 min-w-0'>
          <KTIcon iconName={(doc.icon || 'document').replace(/^ki-/, '')} className='fs-2x text-primary flex-shrink-0' />
          <h1 className='mb-0 text-break'>{content.title}</h1>
        </div>
        <HelpLanguageSelector
          languages={languages}
          currentLang={currentLang}
          onChange={onLangChange}
          availableLanguages={availableLanguages}
        />
      </div>
      <p className='help-short-desc'>{content.shortDescription}</p>

      <HelpTableOfContents markdownBody={content.markdownBody} hasFaqs={hasFaqs} />

      <div className='help-markdown-body'>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h2: createHeadingRenderer(2),
            h3: createHeadingRenderer(3),
            table: ({children}) => (
              <div className='table-responsive'>
                <table className='table table-bordered table-striped'>{children}</table>
              </div>
            ),
            img: ({src, alt}) => (
              <img src={src} alt={alt || 'Help article image'} className='help-img' loading='lazy' />
            ),
          }}
        >
          {content.markdownBody}
        </ReactMarkdown>
      </div>

      {hasFaqs && <HelpFAQSection faqs={content.faqs} />}

      {relatedPages && relatedPages.length > 0 && (
        <div className='help-related-pages'>
          <span className='text-muted fw-semibold me-2'>Related:</span>
          <div className='d-inline-flex flex-wrap gap-2'>
            {relatedPages.map((pageId) => (
              <Link key={pageId} to={`/help/${pageId}`} className='related-link'>
                <KTIcon iconName='document' className='fs-7' />
                {pageId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export {HelpDocumentView}
export type {HelpDocument, Language}
